import React, { PureComponent } from 'react';
import type {
  Circle,
  LatLng,
  LatLngBounds,
  LatLngBoundsExpression,
  LatLngBoundsLiteral,
  LatLngTuple,
  Map,
  Marker,
  Polyline,
  Polygon,
} from 'leaflet';
import type Leaflet from 'leaflet';

const circleConf = {
  color: '#3e87ee',
  fillColor: '#96d270',
  fillOpacity: 0.5,
  radius: 100,
};

export type PortCallEvent = {
  latitude: string | number;
  longitude: string | number;
  name: string | null;
  hasLoad: boolean;
  hasDischarge: boolean;
};

export default class LeafletMap extends PureComponent<{
  point?: [number, number];
  bounds?: LatLngBoundsExpression;
  onChange?: (point: LatLng) => void;
  routes?: { path: { lat: number; lng: number }[]; from: string; to: string }[];
  events?: PortCallEvent[];
  polygons?: {
    name: string;
    polygon: { lat: number; lng: number }[];
  }[];
}> {
  private marker: Circle;
  private point: [number, number] | undefined;
  private map: Map;
  private L: typeof Leaflet;
  private bounds: LatLngBounds | LatLngTuple[] | undefined;
  private polyLines: Polyline[] | undefined;
  private polygons: Polygon[] | undefined;
  private events: Marker[] | undefined;
  private ballast: Marker | undefined;

  async componentDidMount() {
    const point = this.props.point;
    const L = await import('leaflet');
    // This is needed to stop "map container is already initialized." error
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
    const map = L.map('map').setView(
      point || [56.226081072213745, 2.3799562454223637],
      point ? 15 : 4,
    );

    const Ocean_Basemap = L.tileLayer(
      'https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}',
      {
        attribution:
          'Tiles &copy; Esri &mdash; Sources: GEBCO, NOAA, CHS, OSU, UNH, CSUMB, National Geographic, DeLorme, NAVTEQ, and Esri',
        maxZoom: 12,
      },
    );
    const OpenStreetMap_Mapnik = L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    );
    const Esri_WorldImagery = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution:
          'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        minZoom: 11,
        errorTileUrl:
          'data:image/svg+xml;base64,PHN2ZyBmaWxsPSIjRkZGRkZGIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgOCA4IiBlbmFibGUtYmFja2dyb3VuZD0ibmV3IDAgMCA4IDgiIHhtbDpzcGFjZT0icHJlc2VydmUiPjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMy45OTgsOEMxLjkzMSw4LDAuMjUsNy43MywwLjI1LDcuMjQ3YzAsMC4wMjgsMC0wLjIyMiwwLTAuMjUgIGMwLTAuODk2LDAuNjg4LTEuMzg1LDEuMDE4LTEuNTQ0YzAuMzI4LTAuMTU4LDEuMDQ3LTAuMzgsMS4zNDQtMC41NjljMC4yOTktMC4xODksMC4zODUtMC4yNiwwLjM4NS0wLjI2cy0wLjAwNi0wLjAzOSwwLDBWNC4yNDkgIEMyLjc4NywzLjk2OCwyLjY0NiwzLjYyNSwyLjU1NiwzLjIwNGMtMC4wMDIsMC0wLjAwNCwwLjAwNC0wLjAwNCwwLjAwNEMyLjQwOSwzLjI0NywyLjIzNSwzLjA1NiwyLjE2MywyLjc4MSAgQzIuMDkxLDIuNTA5LDIuMTQ2LDIuMjU1LDIuMjg2LDIuMjE2YzAuMDEtMC4wMDEsMC4wMTgsMC4wMDQsMC4wMjcsMC4wMDJDMi4yOTIsMS45MTYsMi4yNDUsMS41MjUsMi4yNDUsMS4zOTcgIGMwLTAuMjA4LTAuMDI5LTAuMzg0LDAuMjM4LTAuNzUzQzIuNzUxLDAuMjc3LDIuOTc0LDAuNDIsMi45NzQsMC40MnMwLjE3Mi0wLjE3NywwLjMzLTAuMjU2YzAuMTU4LTAuMDgsMC41MzYtMC4yOCwxLjIzMS0wLjA3MyAgYzAuNjk0LDAuMjA4LDAuOTYzLDAuMjk3LDEuMTM3LDEuMDk4QzUuNzcyLDEuNjUsNS43MzksMS45OSw1LjcxLDIuMjE4YzAuMDA2LDAsMC4wMS0wLjAwMywwLjAxNi0wLjAwMiAgYzAuMTQzLDAuMDM5LDAuMTk5LDAuMjkzLDAuMTI3LDAuNTY1QzUuNzc4LDMuMDUsNS42MSwzLjIzOCw1LjQ3LDMuMjFDNS4zNzgsMy42NTEsNS4yMjYsMy45NjIsNC45OTcsNC4yNDl2MC4zNzUgIGMwLjAwMi0wLjAxNiwwLDAsMCwwczAuMDgzLDAuMDcxLDAuMzc3LDAuMjZjMC4yOTksMC4xODksMS4wMTgsMC40MTEsMS4zNDcsMC41NjljMC4zMiwwLjE1NCwwLjk5MiwwLjY4MywxLjAyNSwxLjU0NCAgYzAuMDA4LDAuMDIzLDAsMC4yMjgsMCwwLjI1QzcuNzQ2LDcuNzMsNi4wNjUsOCwzLjk5OCw4TDMuOTk4LDh6Ij48L3BhdGg+PC9zdmc+',
      },
    );
    const OpenSeaMap = L.tileLayer(
      'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
      {
        attribution:
          'Map data: &copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors',
      },
    );

    if (point) {
      OpenStreetMap_Mapnik.addTo(map);
    } else {
      Ocean_Basemap.addTo(map);
      Esri_WorldImagery.addTo(map);
    }

    [OpenSeaMap].forEach((layer) => layer.addTo(map));

    new L.Control.Layers(
      {
        'Ocean Base Map': Ocean_Basemap,
        OpenStreetMap_Mapnik,
      },
      {
        OpenSeaMap,
        Esri_WorldImagery,
      },
    ).addTo(map);

    if (point) {
      this.marker = L.circle(point, circleConf).addTo(map);
      this.point = point;
    }
    map.on('click', (e) => {
      if (this.props.onChange) this.props.onChange(e.latlng);
    });
    this.map = map;
    this.L = L;
    L.Icon.Default.imagePath = '/static/';
    this.showEventsAndRoutes(
      this.props.events,
      this.props.routes,
      this.props.polygons,
    );
  }
  showEventsAndRoutes(
    events: PortCallEvent[] | undefined,
    routes:
      | {
          path: { lat: number; lng: number }[];
          from: string;
          to: string;
        }[]
      | undefined,
    polygons?: {
      name: string;
      polygon: { lat: number; lng: number }[];
    }[],
  ) {
    const { L, map } = this;
    if (this.events) {
      this.events.forEach((pl) => pl.remove());
      this.events = undefined;
    }
    if (events) {
      this.events = events.map((e) => {
        const text =
          (e.hasDischarge ? 'Discharge' : '') +
          (e.hasDischarge && e.hasLoad ? ' and ' : '') +
          (e.hasLoad ? 'Load' : '') +
          ' in ' +
          e.name;
        const c = L.marker({
          lat: +e.latitude,
          lng: +e.longitude,
        }).bindPopup(text);
        c.addTo(map);
        return c;
      });
    }
    if (this.polyLines) {
      this.polyLines.forEach((pl) => pl.remove());
      this.polyLines = undefined;
    }
    if (this.ballast) {
      this.ballast.remove();
      this.ballast = undefined;
    }
    if (routes) {
      const latlngs: LatLngBoundsLiteral = [];
      routes.forEach((r) =>
        r.path.forEach(({ lat, lng }) => {
          if (isFinite(lat) && isFinite(lng)) {
            latlngs.push([lat, lng]);
          }
        }),
      );

      const ballastOrigin = latlngs[0];
      const ballastFrom = routes[0]?.from;
      if (
        ballastOrigin &&
        ballastFrom !== undefined &&
        !(events?.[0]?.name === ballastFrom)
      ) {
        const ballast = L.marker(
          {
            lat: +ballastOrigin?.[0],
            lng: +ballastOrigin?.[1],
          },
          {
            opacity: 0.3,
          },
        ).bindPopup('Ballast origin: ' + ballastFrom);
        ballast.addTo(map);
        this.ballast = ballast;
      }

      const bounds = new L.LatLngBounds(latlngs);
      if (bounds.isValid()) {
        map.fitBounds(bounds);
      }

      this.polyLines = routes.map((r) => {
        const text = r.from + ' - ' + r.to;
        const pl = L.polyline(r.path).bindPopup(text);
        pl.addTo(map);
        return pl;
      });
    }
    if (this.polygons) {
      this.polygons.forEach((pg) => pg.remove());
      this.polygons = undefined;
    }
    this.polygons = polygons?.map((p) => {
      const pg = L.polygon(p.polygon, {
        color: 'red',
      }).bindPopup(p.name);
      pg.addTo(map);
      return pg;
    });
  }
  componentDidUpdate() {
    if (!this.L) {
      return;
    }
    const {
      props: { point, bounds, events, routes, polygons },
      point: oldPoint,
      bounds: oldBounds,
      marker,
      map,
      L,
    } = this;
    if (point !== oldPoint) {
      if (marker) {
        marker.remove();
      }
      if (point) {
        this.marker = L.circle(point, circleConf).addTo(map);
        map.panTo(point);
      }
      if (bounds && bounds !== oldBounds) {
        map.fitBounds(bounds);
      }
      this.bounds = bounds;
      this.point = point;
    }
    this.showEventsAndRoutes(events, routes, polygons);
  }
  componentWillUnmount() {
    if (this.marker) {
      this.marker.remove();
    }
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
  }
  render() {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          id="map"
          style={{
            width: '100%',
            height: '100%',
            minHeight: 500,
            maxHeight: 'calc(100vh - 60px)',
          }}
        />
      </div>
    );
  }
}
